import {
    CheckFillIcon,
    CloseIcon,
    CorpsIcon,
    DeleteIcon,
    EmailIcon,
    HandshakeIcon,
    PhoneIcon,
    UserIcon,
} from 'icons'
import {
    Component,
    createEffect,
    createSignal,
    For,
    onMount,
    Show,
} from 'solid-js'

import { useNavigate, useSearchParams } from '@solidjs/router'
import {
    AdminCooperationCount,
    api_admin_cooperations_count_get,
    api_admin_cooperations_del,
    api_admin_cooperations_list,
    api_admin_cooperations_patch,
    Cooperation,
    CooperationState,
} from 'abi'
import {
    addAlert,
    Counter,
    LoadingDots,
    LoadingElem,
    Pagination,
    setPopup,
} from 'components'
import { createStore, produce } from 'solid-js/store'
import './style/requests.scss'
import { isAdmin } from './users/corp/shared'
import { outletRef } from '.'

const Requests: Component = () => {
    let nav = useNavigate()

    type STATE = {
        count: AdminCooperationCount

        maxPages: number
        page: number

        anim: boolean
        active: CooperationState | null

        loadingC: boolean
        loadingI: boolean

        reqs: Cooperation[]
    }
    const [state, setState] = createStore<STATE>({
        anim: false,
        active: null,

        loadingI: true,
        loadingC: true,

        reqs: [],

        maxPages: -1,
        page: 0,
        count: {
            total: -1,
            approved: -1,
            pending: -1,
            rejected: -1,
        },
    })

    let underlineRef: HTMLDivElement

    const [search_params, setSearchParams] = useSearchParams()

    onMount(() => {
        if (!isAdmin()) return nav('/panel/403', { replace: true })

        getCount()
    })

    createEffect(() => {
        if (!isAdmin()) return

        state.active

        getReqs()

        if (!underlineRef) return

        moveunderline()
    })

    const getCount = async () => {
        setState({ loadingC: true })
        let res = await api_admin_cooperations_count_get()

        if (res.ok()) {
            setState({
                count: res.body,
                maxPages: Math.min(0, Math.floor(res.body.total / 32)),
            })
        }
        setState({ loadingC: false })
    }

    const getReqs = async () => {
        setState({ loadingI: true })

        let page = parseInt(search_params.page || '0')
        setSearchParams({ page })

        let reqsRes = await api_admin_cooperations_list({
            page,

            // TODO: DESIGN FOR ORDERS
            order: 'asc',
            state: state.active,
        })

        if (reqsRes.ok()) {
            setState({ page, reqs: reqsRes.body })
        }

        setState({ loadingI: false })
    }

    const addToState = (state: STATE['active']) => {
        setState(
            produce(s => {
                s.count![state!] += 1
            })
        )
    }
    const removeFromState = (state: STATE['active']) => {
        setState(
            produce(s => {
                if (s.count![state!] <= 0) return
                s.count![state!] -= 1
            })
        )
    }

    const moveunderline = () => {
        if (!state.active) return

        let parent = underlineRef.parentElement

        let target = parent!.querySelector<HTMLElement>(`#${state.active}`)

        let left = target?.offsetLeft

        underlineRef.style.width = `${target?.clientWidth || 0}px`
        underlineRef.style.transform = `translateX(${left}px)`
    }

    interface RequestItemProps extends Cooperation {
        onDel(): void
        addToS(s: STATE['active']): void
        removeFromS(s: STATE['active']): void
    }
    const RequestItem: Component<RequestItemProps> = P => {
        type DATA = {
            loading: boolean
            state: Cooperation['state']
        }

        const [data, setData] = createStore<DATA>({
            loading: false,
            state: P.state,
        })

        let bodyRef: HTMLDivElement

        const [active, setActive] = createSignal(false)

        createEffect(() => {
            if (bodyRef.scrollHeight > 120) {
                bodyRef.classList.add('pointer')
            }

            if (!active()) {
                bodyRef.style.maxHeight = '120px'
                return
            }

            let height = bodyRef.scrollHeight
            if (height < 120) return
            bodyRef.style.maxHeight = `${height}px`
        })

        const LoadingStat = () => {
            return (
                <div class='stat'>
                    <LoadingDots />
                    <p class='label description'>درحال انتظار</p>
                </div>
            )
        }
        const ApprStat = () => {
            return (
                <div class='stat green'>
                    <CheckFillIcon />
                    <p class='label description'>تایید شده</p>
                </div>
            )
        }
        const RejStat = () => {
            return (
                <div class='stat red'>
                    <CloseIcon />
                    <p class='label description'>رد شده</p>
                </div>
            )
        }

        const onState = async (state: Cooperation['state']) => {
            setData('loading', true)

            let res = await api_admin_cooperations_patch(
                { id: P.id },
                { state },
                { show_alert: true }
            )
            setData('loading', false)

            if (!res.ok()) return

            setData('state', state)

            P.removeFromS(P.state)
            P.addToS(state)
        }

        const onDel = async () => {
            setData('loading', true)

            let res = await api_admin_cooperations_del(
                {
                    id: P.id,
                },
                {
                    show_alert: true,
                }
            )

            setData('loading', false)

            if (!res.ok()) return

            addAlert({
                type: 'success',
                subject: 'موفق!',
                content: 'درخواست با موفقیت حذف شد',
                timeout: 3,
            })
            P.onDel()
        }

        return (
            <Show
                when={!data.loading}
                fallback={<LoadingElem class='request-item' />}
            >
                <div
                    class='request-item'
                    classList={{
                        green: data.state == 'approved',
                        yellow: data.state == 'pending',
                        red: data.state == 'rejected',
                    }}
                >
                    <div class='header'>
                        <div class='info'>
                            <p class='corp title_small'>
                                <CorpsIcon />
                                {P.company}
                            </p>
                            <p class='name title_smaller'>
                                <UserIcon />
                                {P.name}
                            </p>
                            <div class='contacts title_smaller'>
                                <Show when={P.email}>
                                    <div class='contact '>
                                        <EmailIcon />
                                        {P.email}
                                    </div>

                                    <Show when={innerWidth > 768}>•</Show>
                                </Show>
                                <a href={`tel:${P.phone}`} class='contact'>
                                    <PhoneIcon />
                                    {P.phone}
                                </a>
                            </div>
                        </div>
                        <div class='status'>
                            <Show when={data.state == 'approved'}>
                                <ApprStat />
                            </Show>
                            <Show when={data.state == 'pending'}>
                                <LoadingStat />
                            </Show>
                            <Show when={data.state == 'rejected'}>
                                <RejStat />
                            </Show>
                        </div>
                    </div>
                    <div
                        class='body description'
                        classList={{ active: active() }}
                        ref={e => {
                            bodyRef = e
                        }}
                        role='button'
                        onclick={e => {
                            if (e.currentTarget.scrollHeight < 120) return
                            setActive(s => !s)
                        }}
                    >
                        {P.detail}
                    </div>
                    <div class='bottom'>
                        <button
                            class='cta rej'
                            disabled={P.state == 'rejected'}
                            onclick={() => {
                                setPopup({
                                    show: true,
                                    type: 'error',
                                    title: 'رد درخواست؟',
                                    content: '',
                                    Icon: CloseIcon,
                                    onSubmit() {
                                        onState('rejected')
                                    },
                                })
                            }}
                        >
                            <CloseIcon />
                        </button>
                        <button
                            class='cta appr'
                            disabled={P.state == 'approved'}
                            onclick={() => {
                                setPopup({
                                    show: true,
                                    type: 'success',
                                    title: 'تایید درخواست؟',
                                    content:
                                        'بعد از تایید شما، برای درخواست کننده پیامک ارسال خواهد شد.',
                                    Icon: CheckFillIcon,
                                    onSubmit() {
                                        onState('approved')
                                    },
                                })
                            }}
                        >
                            <CheckFillIcon />
                        </button>
                        <button
                            class='cta del'
                            onclick={() => {
                                setPopup({
                                    show: true,
                                    type: 'error',
                                    title: 'حذف درخواست؟',
                                    content:
                                        'بعد از حذف امکان بازگشت آن وجود ندارد!',
                                    Icon: DeleteIcon,
                                    onSubmit() {
                                        onDel()
                                    },
                                })
                            }}
                        >
                            <DeleteIcon />
                        </button>
                    </div>
                </div>
            </Show>
        )
    }

    return (
        <div class='requests-section section'>
            <div class='requests-wrapper' classList={{ anim: state.anim }}>
                <header class='head section_title'>
                    <HandshakeIcon />
                    <span onanimationend={() => setState({ anim: true })}>
                        درخواست همکاری
                    </span>
                    <HandshakeIcon />
                </header>

                <div class='counts-wrapper'>
                    <Show
                        when={!state.loadingC}
                        fallback={
                            <div class='total-wrapper loading section_title'>
                                <LoadingDots />
                                <p class='label title_smaller'>تعداد کل</p>
                            </div>
                        }
                    >
                        <div
                            role='button'
                            onclick={() => setState('active', null)}
                            class='total-wrapper section_title'
                        >
                            <Counter end={state.count.total} />
                            <p class='label title_smaller'>تعداد کل</p>
                        </div>
                        <div class='other-counts'>
                            <div
                                role='button'
                                onclick={() => setState('active', 'approved')}
                                class='count green'
                                id='approved'
                            >
                                <p class='counter-span section_title'>
                                    {state.count.approved}
                                </p>

                                <p class='label description'>تایید شده</p>
                            </div>
                            <div
                                role='button'
                                onclick={() => setState('active', 'pending')}
                                class='count'
                                id='pending'
                            >
                                <p class='counter-span section_title'>
                                    {state.count.pending}
                                </p>

                                <p class='label description'>درحال انتظار</p>
                            </div>
                            <div
                                role='button'
                                onclick={() => setState('active', 'rejected')}
                                class='count red'
                                id='rejected'
                            >
                                <p class='counter-span section_title'>
                                    {state.count.rejected}
                                </p>

                                <p class='label description'>رد شده</p>
                            </div>

                            <div
                                class='underline'
                                ref={e => (underlineRef = e)}
                                classList={{ active: !!state.active }}
                            ></div>
                        </div>
                    </Show>
                </div>

                <div class='request-items'>
                    <Show
                        when={!state.loadingI}
                        fallback={
                            <>
                                <LoadingElem class='request-item' />
                                <LoadingElem class='request-item' />
                                <LoadingElem class='request-item' />
                            </>
                        }
                    >
                        <Show
                            when={state.reqs.length !== 0}
                            fallback={<Empty />}
                        >
                            <For each={state.reqs}>
                                {req => (
                                    <RequestItem
                                        {...req}
                                        onDel={() => {
                                            setState(
                                                produce(s => {
                                                    let id = s.reqs.findIndex(
                                                        r => r.id == req.id
                                                    )

                                                    if (id == -1) return

                                                    s.reqs.splice(id, 1)
                                                })
                                            )
                                            removeFromState(req.state)
                                        }}
                                        addToS={addToState}
                                        removeFromS={removeFromState}
                                    />
                                )}
                            </For>
                        </Show>
                    </Show>
                </div>

                <Show when={state.maxPages > 1}>
                    <div class='pagination-container'>
                        <Pagination
                            max_page={state.maxPages}
                            current={state.page || 0}
                            onChange={page => {
                                setSearchParams({ page })
                                outletRef?.scrollTo({
                                    top: 0,
                                    behavior: 'smooth',
                                })
                            }}
                        />
                    </div>
                </Show>
            </div>
        </div>
    )
}

const Empty: Component = () => {
    return (
        <div class='empty-wrapper title'>
            <CloseIcon />
            درخواستی برای نمایش وجود ندارد
        </div>
    )
}

export default Requests
